import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store, select } from '@ngrx/store';
import saveAs from 'file-saver';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { Subject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ActionService } from 'src/app/core/services/action.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { RouteService } from 'src/app/core/services/route.service';
import { CreateApplicationTemplate, UpdateApplicationTemplate, UpdateApplicationTemplateSuccess } from 'src/app/core/state/_actions/application.actions';
import { LoadAllTables } from 'src/app/core/state/_actions/table.actions';
import { ApplicationTemplatePayload, Table } from 'src/app/core/state/_models/application.models';
import { OpTypes } from 'src/app/core/state/_models/general.model';
import { getApplicationOperations } from 'src/app/core/state/_selectors/application.selector';
import { getAllTables, getAllTablesLoading } from 'src/app/core/state/_selectors/table.selector';
import { ApplicationService } from 'src/app/core/state/_services/application.service';
import { ReportService } from 'src/app/core/state/_services/report.service';
import { remoteFileToBase64 } from 'src/app/core/utils/data-processor.util';
import { Roles } from 'src/app/shared/enums/role.enum';
import { DragUpload } from 'src/app/shared/models/components.models';
import { AlertService, ToastTypes } from 'src/app/shared/services/alert.service';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-template-upsert',
  templateUrl: './upsert.component.html',
  styleUrls: ['./upsert.component.scss']
})
export class UpsertComponent implements OnInit, OnDestroy {
  @Input('key') key: string = '';
  @Input('name') name: string = '';
  @Input('description') description: string = '';
  @Input('filename') fileName: string = '';
  @ViewChild('selectTable', { static: true }) selectTable!: TemplateRef<any>;
  tables: Array<Table> | null = null;
  tableForm!: UntypedFormGroup;
  subsink: SubSink = new SubSink();
  templateForm!: UntypedFormGroup;
  selectedBase64: string = '';
  dragValue!: DragUpload;
  newTemplateData: any = null;
  clearDragUpload = new Subject();
  deleteTemplate = false;
  isUpsertComponent: boolean = true;
  existingTemplateName: string = '';
  allowedFileSize:number = 20;
  exceedSizeMessage:string = `The file size exceeds the allowable limit(${ this.allowedFileSize }MB).`
  applicationKey:string = '';
  isTablesLoading:{[key:string]:boolean} = {};

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private report: ReportService,
    private modal: NgbModal,
    private action: ActionService,
    private alert: AlertService,
    private applicationService: ApplicationService,
    private route: RouteService
  ) { }


  ngOnInit(): void {
    this.applicationKey = this.route.getApplicationKey();
    this.getApiState();
    this.initForm();
    this.loadSubscribers();
  }

  getApiState(){
    const combinedSubscription =  this.store.select(getAllTablesLoading).subscribe((data)=>{
      this.isTablesLoading = data
    })
    this.subsink.add(combinedSubscription);
  }
  ngOnDestroy(){
    this.subsink.unsubscribe();
  }

  initForm = () => {
    this.existingTemplateName = this.fileName;
    this.templateForm = this.fb.group({
      Name: [
        this.name || '',
        [
          Validators.required,
          Validators.pattern(/^([a-zA-Z0-9]+(\s+[a-zA-Z0-9]+)*){4,45}$/),
        ],
      ],
      Description: [this.description || ''],
      Template: [null],
    });
    this.tableForm = this.fb.group({
      TableKey: [null, [Validators.required]]
    });
    if (!this.toBeUpdated()) {
      this.templateForm.controls.Template.setValidators([Validators.required]);
      this.templateForm.controls.Template.updateValueAndValidity();
    } else {
      this.templateForm.controls.Template.setValue({ value: [{ value: [], name: this.fileName }] });
      this.dragFileEvent(
        {
          value: [
            {
              value: [],
              name: this.fileName,
            }
          ],
        }
      );
    }
  };

    /**
   * Subscribers
   */
    loadSubscribers = () => {
        this.subsink.add(
          this.store.pipe(
          select(getAllTables),
          filter((tables) => !!tables[this.route.getApplicationKey()]),
          map((tables) => tables[this.route.getApplicationKey()]))
          .subscribe((response: any) => {
            if (
              response != null &&
              response.length > 0
            ) {
              this.tables = response;
            }
          })
        );
  };

  /**
   * @returns if template is to be updated
   */
  toBeUpdated = () => !!this.key;

  /**
   * Submit
   */
  onSubmit = async () => {

    this.templateForm.controls.Template.setValue(this.selectedBase64);
    try {
      let payload = {
        Id: null,
        Key: this.key,
        ApplicationId: null,
        ApplicationKey: this.route.getApplicationKey(),
        Name: this.templateForm.value['Name'].toString(),
        FileName: this.toBeUpdated()? (this.fileName === '' ? this.existingTemplateName:this.fileName.toString()):this.fileName.toString(),
        FileData: this.selectedBase64,
        Description: this.templateForm.value['Description'].toString()
      }
      if (!this.toBeUpdated()) {
        await this.action.dispatch(
          OpTypes.CREATE,
          new CreateApplicationTemplate(payload),
          getApplicationOperations
        );
      } else {
        await this.action.dispatch(
          OpTypes.UPDATE,
          new UpdateApplicationTemplate(payload),
          getApplicationOperations
        );
      }
      this.templateForm.reset();
      this.close();
    } catch (err) { }
  };

  /**
   * Super admin check
   * @returns true if super admin
   */
  isSuperadmin = () => this.authService.userProfile?.Role === Roles.superAdmin;

  /**
   * Get application form controls
   */
  get controls() {
    return this.templateForm.controls;
  }

  updateApplication(payload: ApplicationTemplatePayload): void {
    this.store.dispatch(new UpdateApplicationTemplateSuccess(payload));
  }
  /**
   * Close modal
   */
  close = () => {
    this.modal.dismissAll();
  };

  /**
   * Change logo
   */
  changeTemplate = async () => {
    this.selectedBase64 = (<any>(
      await remoteFileToBase64(`${this.controls.Template.value}`)
    )).value;
  };

  /**
   * Drag file event
   */
  dragFileEvent = (event: any) => {
    this.dragValue = event;
    if (this.dragValue.value.length) {
      this.fileName = event.value[0].name;
      this.selectedBase64 = event.value[0].value;
      if (this.selectedBase64.length)
        this.existingTemplateName = '';
    } else {
      this.fileName = '';
      this.selectedBase64 = '';
    }
    this.templateForm.controls.Template.setValue(this.selectedBase64);
  };

  /**
   * Crop image event
   */
  imageCropped(event: ImageCroppedEvent) {
    this.selectedBase64 = event.base64!;
    this.uriToFile(event.base64!, this.fileName);
  }

  uriToFile = async (inputURL: string, fileName: string = '') => {
    this.deleteTemplate = inputURL.includes('delete');
    const inputURI = inputURL.includes('data:')
      ? inputURL
      : (<any>await remoteFileToBase64(inputURL, false)).value;
    const arr = inputURI.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]);
    let n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    this.fileChangeEvent(
      {
        target: {
          files: [
            new File(
              [u8arr],
              fileName ? fileName : inputURL.split('/').slice(-1).join(),
              {
                type: mime,
              }
            ),
          ],
        },
      }
    );
  };

  fileChangeEvent(event: any): void {
    this.fileName = event.target.files[0].name;
    let formdata = new FormData();
    formdata.append('0', event.target.files[0]);
    this.newTemplateData = formdata;
  }
  uploadTemplate = async (formdata: any) => {
    await this.applicationService
      .createTemplate(formdata)
      .subscribe((res) => (this.selectedBase64 = res.FileName));
  };

  downloadTemplate = async () => {
    this.subsink.add(
    this.report
      .getTemplateFile(
        this.key,
        true
      )
      .pipe(filter((data) => !!data && data.File != undefined))
      .subscribe((data) => {
        this.renderFile(data);
      }));
  }

  editTemplate = async () =>  {
    this.tableForm = this.fb.group({
      TableKey: [null, [Validators.required]]
    });
    if (this.tables == null)
      this.store.dispatch(new LoadAllTables(this.route.getApplicationKey()));
    const tableModal = this.modal.open(this.selectTable, {
      windowClass: 'modalField-custom',
      centered: true,
      backdrop: 'static',
      animation: true,
      keyboard: false,
      size: 'sm'
    });
  }

  onSelectTableSubmit = () => {
    window.open(
      `/apps/${this.route.getApplicationKey()}/settings/templates/edit/${this.tableForm.controls.TableKey.value}/${this.key}`,
      '_blank',
      'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,width=1800,height=800,left=0,top=0'
    );
  }

  getTables = (): Array<any> | null =>
    this.tables
      ? this.tables.map((table) => {
        return { value: table.Key, label: table.Name };
      })
      : null;


  renderFile = (data: any) => {
    if (data.FileName != '') {
      saveAs(data.File, data.FileName);
    } else {
      this.alert.toast(
        ToastTypes.ERROR,
        'Not Found!',
        'There is no document to download.'
      );
    }
  }

}
